import { merge } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';
import { computeTotalPrice } from 'hb-react/shared/apps/Events/utils/priceAndPayments';

import { useEventContext } from './EventContext';
import { RegistrationSteps } from '../constants/registrationSteps';
import { getSelectedCurrency } from '../utils/getTicketInformation';

export const RegistrationFlowContext = createContext(null);

export const useRegistrationFlowContext = () =>
  useContext(RegistrationFlowContext);

export const RegistrationFlowProvider = ({ children }) => {
  const { event } = useEventContext();
  const { data } = useEventCartContext() || {};

  const [registrationFlow, setRegistrationFlow] = useState({
    currentStep: RegistrationSteps.TICKET_SELECTION_STEP,
  });
  const [valuesStep1, setValuesStep1] = useState({ tickets: [] });
  const [valuesStep2, setValuesStep2] = useState([{}]);
  const [valuesStep3, setValuesStep3] = useState([{}]);

  const cartItems = data?.cart?.cart_items || [];

  const getFormData = useCallback(
    (formData = {}) => {
      const values = merge({}, valuesStep2, valuesStep3, formData);

      return {
        ...values,
        network_events_booking: {
          donations_attributes:
            values.network_events_booking?.donations_attributes || [],
          attendees_attributes: [
            ...values.network_events_booking.attendees_attributes.map(
              (attendee) => ({
                ...attendee,
                sub_attendees: attendee.addons_attributes
                  .filter(({ quantity }) => Number(quantity) > 0)
                  .map((addon) => ({
                    addon_id: addon.id,
                    ...addon,
                  })),
              }),
            ),
            ...values.network_events_booking.addons_attributes
              .filter(({ quantity }) => Number(quantity) > 0)
              .map((addon) => ({
                firstname: values?.receipt?.firstname,
                lastname: values?.receipt?.lastname,
                email: values?.receipt?.email,
                addon_id: addon.id,
                ...addon,
              })),
          ],
        },
      };
    },
    [valuesStep2, valuesStep3],
  );

  const updateTicket = (ticket, payload) => {
    const index = valuesStep1.tickets.findIndex(({ id }) => id === ticket.id);

    if (index < 0) {
      setValuesStep1({
        tickets: [...valuesStep1.tickets, { ...ticket, ...payload }],
      });

      return;
    }

    setValuesStep1({
      tickets: [
        ...valuesStep1.tickets.slice(0, index),
        { ...ticket, ...payload },
        ...valuesStep1.tickets.slice(index + 1),
      ],
    });
  };

  const updateRegistrationFlow = (newRegistrationFlowState) =>
    setRegistrationFlow((prevState) => ({
      prevState,
      ...newRegistrationFlowState,
    }));

  const providerState = useMemo(() => {
    const {
      attendees_attributes: attendees = [],
      addons_attributes: addons = [],
      donations_attributes: donations = [],
    } = valuesStep2?.network_events_booking || {};

    const promoCodesDischarge =
      cartItems
        .filter((item) => item.line_itemable_type === 'Ecommerce::PromoCode')
        .reduce((acc, item) => acc + item.total_in_cents, 0) / 100;

    const totalPrice = computeTotalPrice({
      selectedTickets: valuesStep1.tickets,
      availableTickets: event.tickets,
      attendees,
      addons,
      donations,
    });

    const isShippable = [
      ...attendees.flatMap((attendee) => attendee.addons_attributes),
      ...addons,
    ].find(
      ({ quantity, is_shippable: isShippable }) => quantity > 0 && isShippable,
    );

    return {
      registrationFlow,
      updateRegistrationFlow,
      valuesStep1,
      setValuesStep1,
      updateTicket,
      valuesStep2,
      setValuesStep2,
      valuesStep3,
      setValuesStep3,
      selectedCurrency: getSelectedCurrency({
        event,
        tickets: valuesStep1.tickets,
      }),
      totalPrice: totalPrice + promoCodesDischarge,
      getFormData,
      isShippable,
    };
  }, [valuesStep1, valuesStep2, valuesStep3, registrationFlow, cartItems]);

  return (
    <RegistrationFlowContext.Provider value={providerState}>
      {children}
    </RegistrationFlowContext.Provider>
  );
};
